import { Checkbox, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useWindowDimensions } from "../components/WindowDimensions";
import { I18N } from "../I18N";
import { ImprovementItem } from "../models/dtos";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import useViewportMode from "../hooks/useViewportMode";

export interface ImprovementPlanSelectionProps {
    data: ImprovementItem[];
    instructions: string;
    selectedIds?: string[];
    onSelectionChange: (ids: string[]) => Promise<void> | void;
}

const ImprovementPlanSelectionComponent = ({ data, instructions, selectedIds, onSelectionChange }: ImprovementPlanSelectionProps) => {

    const { height } = useWindowDimensions();
    const viewport = useViewportMode();

    const handleRowClicked = (ids: string[]) => {
        onSelectionChange(ids);
    }

    const toggleSelection = (item: ImprovementItem) => {
        const itemId = item.Proficiency.Id;
        const currentIds = selectedIds ?? [];
        handleRowClicked(currentIds.includes(itemId)
            ? currentIds.filter(id => id !== itemId)
            : [...currentIds, itemId]);
    };

    const renderImprovementItemCard = (item: ImprovementItem) => (
        <MobileEntityCard
            ariaLabel={`Improvement item: ${item.Proficiency.Name}`}
            title={item.Proficiency.Name}
            status={<Checkbox
                checked={(selectedIds ?? []).includes(item.Proficiency.Id)}
                onChange={() => toggleSelection(item)}
                inputProps={{ 'aria-label': `Select improvement item: ${item.Proficiency.Name}` }}
                sx={{ width: 44, height: 44 }}
            />}
            fields={[
                { label: I18N.Translate('Current Selection'), value: item.CurrentSelection },
                { label: I18N.Translate('Next Selection'), value: item.NextSelection },
            ]}
        />
    );

    const desktopGrid = (
        <div style={{ height: height - 260, width: '100%' }}>
            <DataGridPro
                sx={{
                    "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                        display: "none"
                    }
                }}
                density="compact"
                checkboxSelection={true}
                onSelectionModelChange={handleRowClicked}
                selectionModel={selectedIds}
                rows={data.map(x => { return { ...x, id: x.Proficiency.Id } })}
                hideFooter={true}
                columns={[
                    {
                        field: 'Name',
                        headerName: I18N.Translate('Name'),
                        flex: 1,
                        renderCell: ({ row: r }) => r.Proficiency.Name
                    },
                    {
                        field: 'CurrentSelection',
                        headerName: I18N.Translate('Current Selection'),
                        width: 140
                    },
                    {
                        field: 'NextSelection',
                        headerName: I18N.Translate('Next Selection'),
                        width: 120
                    },
                ]}
            />
        </div>
    );

    return (
        <>
            <Typography sx={{ marginLeft: '1em', marginBottom: '.5em' }}>{instructions}</Typography>
            <ResponsiveCollection
                viewMode={viewport.isTablet && viewport.isPortrait ? 'phone' : viewport.mode}
                items={data}
                getKey={item => item.Proficiency.Id}
                renderCard={renderImprovementItemCard}
                desktop={desktopGrid}
                ariaLabel="Improvement plan items"
                emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No improvement items available</Typography>}
            />
        </>
    );
}

export default ImprovementPlanSelectionComponent;
